<template>
  <div>
    <div v-if="hasAvailabilities || editModeActive">
      <div class="flex justify-between items-baseline">
        <p class="text-strong-body">Calendar Availability</p>
        <poppins-button v-if="editModeActive" value="Edit" variant="text" :href="`/availability`" />
      </div>
    </div>
    <p v-if="editModeActive" class="text-small text-grey-body">
      For when you have specific days that deviate from your general schedule
    </p>
    <div v-if="hasAvailabilities || editModeActive" class="row row--flush !mb-0 mt-24">
      <div class="col" :class="alwaysMobileVariant ? 's12' : 's12 m6'">
        <v-calendar
          id="availability-calendar"
          expanded
          trim-weeks
          :min-date="new Date()"
          :initial-page="fromPage"
          :attributes="calendarAttributes"
          :masks="masks"
          @dayclick="triggerDateSelected"
          @did-move="onDidMove"
        ></v-calendar>
      </div>
      <div class="col" :class="alwaysMobileVariant ? 's12 mt-24' : 's12 m6'">
        <div v-if="hasSelectedAvailabilities" class="c-pane-container--list">
          <ul>
            <calendar-card
              v-for="availability in selectedAvailabilities"
              :key="availability.cursor"
              :availability="availability"
            />
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { endOfMonth, isSameDay, isSameMonth, parseISO, startOfDay, startOfMonth } from 'date-fns';
import { Calendar } from 'v-calendar';
import 'v-calendar/dist/style.css';
import { ACCOUNT_AVAILABILITIES_QUERY } from '../../graphql/availability/queries';
import { drill } from '../../lib/objectTools';
import CalendarCard from './CalendarCard.vue';
import PoppinsButton from '@/components/button/Button.vue';

export default {
  name: 'AvailabilityCalendar',
  components: {
    CalendarCard,
    VCalendar: Calendar,
    PoppinsButton,
  },
  props: {
    accountId: {
      type: String,
      required: true,
    },
    alwaysMobileVariant: {
      type: Boolean,
      required: false,
      default: false,
    },
    editModeActive: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update-calendar-availability'],
  data(): any {
    const now = new Date();
    return {
      account: null,
      selectedDate: null,
      fromPage: { month: now.getMonth() + 1, year: now.getFullYear() },
    };
  },
  apollo: {
    account: {
      query: ACCOUNT_AVAILABILITIES_QUERY,
      variables(): any {
        return {
          id: this.accountId,
          kind: 'SITTER',
          afterTime: this.windowStart.toISOString(),
          beforeTime: this.windowEnd.toISOString(),
        };
      },
    },
  },
  computed: {
    hasAvailabilities(): boolean {
      return this.availabilities && this.availabilities.length > 0;
    },
    hasSelectedAvailabilities(): boolean {
      return this.selectedAvailabilities && this.selectedAvailabilities.length > 0;
    },
    availabilities(): Array<any> | null {
      return drill(this.account, ['availabilities', 'edges']);
    },
    masks(): object {
      return { title: 'MMM YYYY' };
    },
    highlightDate(): Date {
      if (this.selectedDate) {
        return this.selectedDate.date;
      }

      if (this.hasAvailabilities) {
        return parseISO(this.availabilities[0].node.start);
      }

      return new Date();
    },
    calendarAttributes(): Array<object> | undefined {
      const today = new Date();
      let attributes: Array<object> = [
        {
          highlight: {
            style: {
              backgroundColor: '#e0e0e0',
            },
          },
          content: {
            style: {
              color: '#05070f',
            },
          },
          dates: today,
        },
      ];
      if (!this.hasAvailabilities) return attributes;

      attributes = attributes.concat([
        {
          highlight: {
            style: {
              backgroundColor: '#99005C',
            },
          },
          content: {
            style: {
              color: '#ffffff',
            },
          },
          dates: this.highlightDate,
        },
      ]);

      const availabilityStartDates: Array<Date> = [];
      const bookedStartDates: Array<Date> = [];

      this.availabilities.forEach((avail) => {
        if (avail.node.kind === 'AVAILABLE') {
          availabilityStartDates.push(parseISO(avail.node.start));
        } else if (avail.node.kind === 'BOOKED') {
          bookedStartDates.push(parseISO(avail.node.start));
        }
      });

      return attributes.concat([
        {
          dot: {
            style: { backgroundColor: '#76787e', marginBottom: '-8px' },
          },
          dates: bookedStartDates,
        },
        {
          dot: {
            style: { backgroundColor: '#229150', marginBottom: '-8px' },
          },
          dates: availabilityStartDates,
        },
      ]);
    },
    selectedAvailabilities(): Array<any> {
      if (!this.availabilities) return [];

      return this.availabilities.filter((avail) => isSameDay(parseISO(avail.node.start), this.highlightDate));
    },
    visibleMonthDate(): Date {
      return new Date(this.fromPage.year, this.fromPage.month - 1, 1);
    },
    windowStart(): Date {
      const today = startOfDay(new Date());
      if (isSameMonth(this.visibleMonthDate, today)) {
        return today;
      }
      return startOfMonth(this.visibleMonthDate);
    },
    windowEnd(): Date {
      return endOfMonth(this.visibleMonthDate);
    },
  },
  watch: {
    hasAvailabilities(newVal) {
      this.$emit('update-calendar-availability', newVal);
    },
  },
  methods: {
    triggerDateSelected(e) {
      this.selectedDate = e;
    },
    // Keep the availability window in sync when the calendar navigates to a new month.
    onDidMove(pages: Array<{ month: number; year: number }>) {
      const page = pages?.[0];
      if (!page) return;
      if (page.month === this.fromPage.month && page.year === this.fromPage.year) return;
      this.fromPage = { month: page.month, year: page.year };
      this.selectedDate = null;
    },
  },
};
</script>

<style lang="scss">
#availability-calendar {
  font-family: 'Graphik Web', Helvetica, Arial, sans-serif;
  &.vc-container {
    border: 0;
  }

  .vc-weekday,
  .vc-title,
  .vc-day {
    color: #05070f;
  }

  .vc-title {
    font-size: 16px;
    text-transform: uppercase;
  }

  .vc-header {
    margin-bottom: 1rem;
    justify-content: center;
  }

  .vc-weekdays {
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #f0f1f1;
  }

  .vc-day.is-not-in-month .vc-day-content {
    color: #a5a7aa;
    opacity: 1;
  }

  .vc-day {
    margin-bottom: 8px;
  }

  .vc-day-content.is-disabled {
    color: #6e6f74;
  }
}
</style>
